
import {Options, Vue} from 'vue-class-component'
import {isDefined, scrollToTop} from "@/utils";
import LazyLoadContainer from "@/components/LazyLoadComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {Services} from "@/Services";
import {SpeechContainer} from "@/entities/SpeechContainer";
import {SpeechFilter} from "@/entities/SpeechFilter";
import {Observable, of, zipAll} from 'rxjs';
import SpeakerDetailDialog from "@/components/SpeakerDetailDialog.vue";
import {Speech} from './entities/Speech';
import moment from 'moment';
import {Watch} from "vue-property-decorator";
import Stand from '@/entities/Stand';
import StandDialog from '@/components/StandDialog.vue';
import {ExhibitorContainer} from "@/entities/ExhibitorContainer";
import ExhibitorDetailDialog from "@/components/ExhibitorDetailDialog.vue";
import {Role} from "@/entities/Role";
import {Speaker} from "@/entities/Speaker";

const ics = require('ics');

@Options({components: {ExhibitorDetailDialog, SpeakerDetailDialog, LazyLoadContainer, LoadingSpinner, StandDialog}})
export default class App extends Vue {


  searchValue: string = '';
  isLoading: boolean = true;
  speechContainer: SpeechContainer = null;
  speechFilter: SpeechFilter = new SpeechFilter({});
  speakers: Speaker[] = []
  activeFilters: Record<string, string> = {};
  isFilterActive: boolean = false;
  exhibitorContainer: ExhibitorContainer = null;
  roles: Role[] = [];


  constructor(props: any) {
    super(props);

    let themes: string[] = null;
    const appRoot = document.getElementById('egcf-program-list');
    if (isDefined(appRoot)) {
      const themesAttribute = appRoot.attributes.getNamedItem('themes');
      if (themesAttribute !== null) {
        themes = themesAttribute.value.split('|');
      }
    }
    const observables: Observable<any>[] = [
      Services.getSpeechFilters(themes),
      Services.getSpeeches(themes),
      Services.getExhibitors(),
      Services.getSpeakers(),
      Services.getRoles()
    ];

    of(...observables).pipe(zipAll()).subscribe(values => {
      this.speechFilter = values[0];
      this.speechContainer = values[1];
      this.exhibitorContainer = values[2];
      this.speakers = values[3];
      this.roles = values[4];
      this.isLoading = false;
    })
  }

  mounted() {
    window.addEventListener('scroll', this.handleScrollEvent);
  }

  handleScrollEvent(): void {
    if (isDefined(this.$refs.scrollToTopButton)) {
      if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "block";
      } else {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "none";
      }
    }
  }

  @Watch('isLoading')
  onLoadingChanged() {
    if (!this.isLoading) {
      this.$nextTick(() => {
        this.addClickListenerToSpeakerNamesFromInfo();
      })
    }
  }

  private addClickListenerToSpeakerNamesFromInfo(): void {
    const allElements = document.getElementsByTagName('div');

    for (let i = 0, n = allElements.length; i < n; i++) {
      const attribute = allElements[i].getAttribute('data-speaker-id');
      if (isDefined(attribute)) {
        if (allElements[i].hasChildNodes()) {
          const speakerName = allElements[i].getElementsByTagName('b');
          speakerName[0].style.cursor = 'pointer';
        }
        const speaker = this.getSpeakerById(attribute);
        if (isDefined(speaker)) {
          allElements[i].onclick = (event) => {
            this.openSpeakerDetailDialog(speaker, event);
          }
        }
      }
    }
  }

  scrollTop(): void {
    scrollToTop();
  }

  onSearchValueInput(event): void {
    if (isDefined(this.speechContainer)) {
      this.speechContainer.filterSpeeches(this.searchValue, this.activeFilters);
    }
  }

  toggleFilter(): void {
    this.isFilterActive = !this.isFilterActive;
    if (!this.isFilterActive) {
      this.resetFilter();
    }
  }

  resetFilter(): void {
    this.activeFilters = {};
    this.onFilterChanged();
  }

  onFilterChanged(event?): void {
    for (const [key, value] of Object.entries(this.activeFilters)) {
      if (value.length === 0) {
        delete this.activeFilters[key];
      }
    }
    if (isDefined(this.speechContainer)) {
      this.speechContainer.filterSpeeches(this.searchValue, this.activeFilters)
    }
  }

  hasActiveFilter(): boolean {
    return Object.entries(this.activeFilters).length > 0;
  }

  getLangForSpeechById(id): string {
    if (isDefined(id) && isDefined(this.speechFilter)) {
      for (const [key, value] of Object.entries(this.speechFilter.filters)) {
        for (const filter of value) {
          if (filter.id === id) {
            return filter.title;
          }
        }
      }
    }
    return undefined;
  }

  openStandDialog(stand: Stand, event: Event): void {
    event.stopImmediatePropagation();
    console.log(stand)
    if (isDefined(this.$refs.standDialog)) {
      (this.$refs.standDialog as StandDialog).openDialog(stand);
    }
  }

  openExhibitorDialog(exhibitorId: string, event: Event): void {
    event.stopImmediatePropagation();
    if (isDefined(this.$refs.exhibitorDetailDialog)) {
      const exhibitor = this.exhibitorContainer.exhibitors.find(e => e.id === exhibitorId);
      (this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog).openDialog(exhibitor);
    }
  }

  getSpeakerById(id: string): Speaker {
    if (isDefined(this.speakers) && isDefined(id)) {
      const index = this.speakers.findIndex(s => s.id === id);
      if (index > -1) {
        return this.speakers[index];
      }
    }
    return undefined;
  }


  openSpeakerDetailDialog(speaker: Speaker, event: Event): void {
    if (isDefined(event)) {
      event.stopImmediatePropagation()
    }

    if (isDefined(this.$refs.speakerDetailDialog)) {
      let speechesForSpeaker: Speech[] = this.speechContainer.getSpeechesForSpeakerById(speaker.id);
      (this.$refs.speakerDetailDialog as SpeakerDetailDialog).openDialog(speaker, speechesForSpeaker);
    }
  }

  downloadIcal(speech: Speech, event: Event): void {
    event.stopImmediatePropagation();
    const start = moment(speech.begin);
    const end = moment(speech.end);

    let title = speech.title;
    const prefix = process.env.VUE_APP_PROJECT_NAME_FOR_ICAL;
    if (isDefined(prefix) && prefix.length > 0) {
      title = `${prefix} ${speech.title}`
    }

    ics.createEvent({
      start: [start.year(), start.month() + 1, start.date(), start.hour(), start.minute()],
      end: [end.year(), end.month() + 1, end.date(), end.hour(), end.minute()],
      title: title,
      location: speech.place
    }, ((error, value) => {
      if (error) {
        console.log(error);
        return;
      }
      const blob = new Blob([value], {type: 'text/calendar'});
      const icsFile = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = icsFile;
      a.target = '_blank';
      a.download = speech.title;
      a.type = 'text/calendar';
      a.click();
    }));
  }

}
